import request from '@apps-common/js-client/api/request';

const live = 'live';
const next = 'next';

let tier = import.meta.env.VITE_API_TIER;
if (!tier) {
  tier = location.search === '?live' ? live : next;
}

export const urls = {
  mosaics: 'https://api.planet.com/basemaps/v1',
  mosaicTiles: 'https://tiles{0-3}.planet.com/basemaps/v1/planet-tiles',
  imageTiles: 'https://tiles{0-3}.planet.com/data/v1',
  sif:
    import.meta.env.VITE_API_URL ||
    (tier === live
      ? 'https://api.planet.com/analytics'
      : 'https://sif-next.prod.planet-labs.com'),
};

function getNextLink(links) {
  if (!links) {
    return '';
  }
  for (const link of links) {
    if (link.rel === 'next') {
      return link.href;
    }
  }
  return '';
}

export function getVectorTilesURL(subscriptionId, query, token) {
  const params = new URLSearchParams(query);
  params.set('token', token);

  return `${urls.sif}/tiles/${subscriptionId}/{z}/{x}/{y}?${params}`;
}

export function getImageTilesURL(itemType, itemId, token) {
  return `${urls.imageTiles}/${itemType}/${itemId}/{z}/{x}/{y}?token=${token}`;
}

export function getImageThumbnailURL(itemType, itemId, token) {
  return `https://tiles.planet.com/data/v1/item-types/${itemType}/items/${itemId}/thumb?token=${token}`;
}

export function getMosaicTilesURL(mosaicName, token) {
  return `${urls.mosaicTiles}/${mosaicName}/gmap/{z}/{x}/{y}?token=${token}`;
}

export function getMosaicThumbnailURL(mosaicId, token) {
  return `https://tiles.planet.com/basemaps/v1/planet-tiles/${mosaicId}/thumb?token=${token}`;
}

// removes the UDM & UDM2 indexing subscriptions that are used internally.
// It leverages the title prefix pattern for the subscription:
//
// `index-udmX [reference_feed_id] [sub_title]` where X = empty (i.e.: "") or 2.
function filterUdmSubscriptions(subscriptions) {
  return subscriptions.filter(sub => !sub.title.match(/index-udm(|2)\s{1}/g));
}

export function getFeed(id) {
  const url = `${urls.sif}/feeds/${id}`;
  return request.get(url).then(response => response.data);
}

function fetchAllPages(url, key = 'data') {
  return new Promise(resolve =>
    request.get(url).then(response => {
      const next = getNextLink(response.data.links);
      if (next) {
        return fetchAllPages(next, key).then(data => {
          resolve([...response.data[key], ...data]);
        });
      }
      resolve(response.data[key]);
    }),
  );
}

export function getFeeds() {
  const url = `${urls.sif}/feeds/`;
  return fetchAllPages(url);
}

export function getSubscription(id) {
  const url = `${urls.sif}/subscriptions/${id}`;
  return request.get(url).then(response => response.data);
}

export function getSubscriptions() {
  const url = `${urls.sif}/subscriptions/`;
  return fetchAllPages(url).then(subs => filterUdmSubscriptions(subs));
}

export function getMosaics(seriesId) {
  const url = `${urls.mosaics}/series/${seriesId}/mosaics`;
  return request.get(url).then(response => response.data.mosaics);
}

export function getObservations(subscriptionId, startDate, endDate, bbox) {
  const url = new URL(`${urls.sif}/subscriptions/${subscriptionId}/counts`);
  if (startDate && endDate) {
    let time;
    if (startDate.getTime() === endDate.getTime()) {
      time = startDate.toISOString();
    } else {
      time = `${startDate.toISOString()}/${endDate.toISOString()}`;
    }
    url.searchParams.set('time', time);
  }
  if (bbox) {
    url.searchParams.set('bbox', bbox.map(c => c.toFixed(5)).join(','));
  }
  return request.get(url).then(response => response.data);
}

export function getItems(subscriptionId, startDate, endDate, bbox) {
  const url = new URL(`${urls.sif}/collections/${subscriptionId}/items/`);
  let time;
  if (startDate.getTime() === endDate.getTime()) {
    time = startDate.toISOString();
  } else {
    time = `${startDate.toISOString()}/${endDate.toISOString()}`;
  }
  url.searchParams.set('datetime', time);
  if (bbox) {
    url.searchParams.set('bbox', bbox.map(c => c.toFixed(5)).join(','));
  }
  return fetchAllPages(url, 'features');
}

export function fetchSeriesInfo(seriesId) {
  const url = `https://api.planet.com/basemaps/v1/series/${seriesId}/?v=1.5`;
  return request.get(url).then(response => response.data);
}

export function fetchSeriesMosaics(seriesId) {
  const url = `https://api.planet.com/basemaps/v1/series/${seriesId}/mosaics?v=1.5`;
  return request(url).then(response => response.data.mosaics);
}
